// Formulários respondidos aguardando validação, em cards no formato do quadro de atendimentos
// (cor pelo tempo aberto desde a resposta). Clique abre a resposta para validar.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { Card, CardActionArea, CardContent, Chip, Grid, TextField, Typography, makeStyles } from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { DialogCard, dataBR, duracao, minutosDesde } from "../comum";
import { EVENTO_CARD } from "../CardDrawer";
import { DetalheResposta, rotuloStatus } from "../Formularios";

export const EVENTO_FORMULARIOS = "formularios-validar-mudou";

const useStyles = makeStyles((theme) => ({
  grade: { padding: theme.spacing(1), overflowY: "auto", ...theme.scrollbarStyles },
  card: { borderLeft: `4px solid ${theme.palette.success.main}`, height: "100%" },
  demorado: { borderLeftColor: theme.palette.warning.main },
  atrasado: { borderLeftColor: theme.palette.error.main },
  topo: { display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8 },
  filtros: { display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" },
  legenda: { display: "flex", gap: 14, flexWrap: "wrap", fontSize: 12, color: theme.palette.text.secondary, margin: theme.spacing(0, 1) },
  ponto: { display: "inline-block", width: 10, height: 10, borderRadius: 2, marginRight: 4, verticalAlign: "middle" },
  status: {
    color: theme.palette.text.secondary, fontSize: 12.5, borderTop: `1px dashed ${theme.palette.divider}`,
    marginTop: 6, paddingTop: 4,
  },
}));

const FormulariosValidar = () => {
  const c = useStyles();
  const [itens, setItens] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [form, setForm] = useState("");
  const [resposta, setResposta] = useState(null);
  const [card, setCard] = useState(null);

  const carregar = useCallback(async () => {
    try {
      const { data } = await suporteApi.get("/api/formularios/respostas", { params: { status: "RESPONDIDO", por_pagina: 200 } });
      setItens(data.itens);
      window.dispatchEvent(new Event(EVENTO_FORMULARIOS));
    } catch (err) {
      toast.error(mensagemErro(err));
    }
  }, []);
  useEffect(() => {
    carregar();
    const t = setInterval(() => !document.hidden && carregar(), 15000);
    window.addEventListener(EVENTO_CARD, carregar);
    return () => {
      clearInterval(t);
      window.removeEventListener(EVENTO_CARD, carregar);
    };
  }, [carregar]);

  const todos = itens || [];
  const nomes = [...new Set(todos.map((e) => e.nome_formulario))].sort();
  const f = filtro.trim().toLowerCase();
  const lista = todos
    .filter((e) => !form || e.nome_formulario === form)
    .filter((e) => !f || [e.nome_formulario, e.identificacao, e.conta, e.filial, e.tecnico, e.qra, e.os, e.enviado_por]
      .join(" ").toLowerCase().includes(f))
    .sort((a, b) => String(a.respondido_em).localeCompare(String(b.respondido_em)));  // mais antigos primeiro

  return (
    <MainContainer>
      <MainHeader>
        <Title>{`Formulários para validar (${lista.length})`}</Title>
        <MainHeaderButtonsWrapper>
          <TextField size="small" placeholder="Filtrar nome, loja, O.S, técnico…" value={filtro}
            onChange={(e) => setFiltro(e.target.value)} />
        </MainHeaderButtonsWrapper>
      </MainHeader>
      {nomes.length > 1 && (
        <div className={c.filtros} style={{ margin: "0 8px 6px" }}>
          <Chip size="small" clickable color={!form ? "primary" : "default"} label={`Todos (${todos.length})`} onClick={() => setForm("")} />
          {nomes.map((n) => (
            <Chip key={n} size="small" clickable color={form === n ? "primary" : "default"} onClick={() => setForm(n)}
              label={`${n} (${todos.filter((e) => e.nome_formulario === n).length})`} />
          ))}
        </div>
      )}
      <div className={c.legenda}>
        <span><i className={c.ponto} style={{ background: "#4caf50" }} />até 1 h</span>
        <span><i className={c.ponto} style={{ background: "#ff9800" }} />1 a 4 h</span>
        <span><i className={c.ponto} style={{ background: "#f44336" }} />mais de 4 h</span>
      </div>
      <Grid container spacing={2} className={c.grade}>
        {itens && lista.length === 0 && (
          <Grid item xs={12}><Typography color="textSecondary">Nenhum formulário aguardando validação.</Typography></Grid>
        )}
        {lista.map((e) => {
          const min = minutosDesde(e.respondido_em);
          const classe = min > 240 ? c.atrasado : min > 60 ? c.demorado : "";
          return (
            <Grid item xs={12} sm={6} md={4} lg={3} key={e.id}>
              <Card variant="outlined" className={`${c.card} ${classe}`}>
                <CardActionArea onClick={() => setResposta(e.id)} style={{ height: "100%" }}>
                  <CardContent>
                    <div className={c.topo}>
                      <Typography variant="subtitle1" noWrap>
                        <b>{e.identificacao || e.filial || e.conta || (e.qra != null ? `QRA ${e.qra}` : "Resposta")}</b>
                      </Typography>
                      <Typography variant="caption" color="textSecondary" style={{ whiteSpace: "nowrap" }}>{duracao(min)}</Typography>
                    </div>
                    <Typography variant="body2"><b>{e.nome_formulario}</b></Typography>
                    <Typography variant="caption" color="textSecondary" component="div">
                      {[e.os && `O.S ${e.os}`, e.qra != null && `QRA ${e.qra} ${e.tecnico || ""}`, e.atendimento_id ? `card #${e.atendimento_id}` : "link aberto"]
                        .filter(Boolean).join(" · ")}
                    </Typography>
                    <Typography variant="body2" color="primary" style={{ marginTop: 4 }}>
                      <b>{`${(e.rotulos || {}).botao_validar || "Validar"} →`}</b>
                    </Typography>
                    <div className={c.status}>{`${rotuloStatus(e)} · respondido ${dataBR(e.respondido_em)}`}</div>
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <DetalheResposta id={resposta} onClose={() => { setResposta(null); carregar(); }} onMudou={carregar}
        onAbrirCard={(id) => { setResposta(null); setCard(id); }} />
      <DialogCard id={card} open={!!card} onClose={() => { setCard(null); carregar(); }} onMudou={carregar} />
    </MainContainer>
  );
};

export default FormulariosValidar;
